import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import HelpTip from '../../../components/HelpTip';
import Modal from '../../../components/Modal';
import { tosAccelerate } from '../../../utils/tosAccelerate';
import { GenerationTemplate, ImageModel, LLMProvider, LLMRoleConfigMap, VideoModel } from '../../../types';
import {
  DEFAULT_CHAR_STATE_PROMPT_PREFIX,
  DEFAULT_FIXED_IMAGE_PROMPT,
  MAX_TOTAL_REF_IMAGES,
  parseRefImages,
} from '../utils/editDefaults';
import BaseImageConfigSection from './BaseImageConfigSection';
import LLMRoleOverridesSection from './LLMRoleOverridesSection';
import { buildLLMRoleOverrideOptions } from './llmRoleOverrideOptions';
import PlatformModelSelectSection from './PlatformModelSelectSection';
import PromptTextareaSection from './PromptTextareaSection';
import StatePromptPrefixSection from './StatePromptPrefixSection';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface StyleConfigModalProps {
  open: boolean;
  saving: boolean;
  projectId?: string;
  /** false = 硬强制模式：隐藏 ✕/取消、禁用 Esc。默认 true。 */
  dismissible?: boolean;
  t: Translate;
  generationTemplates: GenerationTemplate[];
  pendingTemplate: GenerationTemplate | null;
  selectedTemplateId: string;
  activeImageModels: ImageModel[];
  activeVideoModels: VideoModel[];
  editImagePlatform: string;
  editImageModel: string;
  editBaseImagePlatform: string;
  editBaseImageModel: string;
  editVideoPlatform: string;
  editVideoModel: string;
  editCharFixedPrompt: string;
  editCharProjectRefImages: string[];
  editSceneFixedPrompt: string;
  editSceneProjectRefImages: string[];
  editPropFixedPrompt: string;
  editPropProjectRefImages: string[];
  editCharStatePromptPrefix: string;
  editSceneStatePromptPrefix: string;
  editPropStatePromptPrefix: string;
  editCharStateImageRatio: string;
  editSceneStateImageRatio: string;
  editPropStateImageRatio: string;
  editVideoPrefixPrompt: string;
  editVideoExtraPrompt: string;
  editBaseConfigCompleted: boolean;
  llmProviders: LLMProvider[];
  editLLMRoleOverrides: Partial<LLMRoleConfigMap>;
  setEditLLMRoleOverrides: Dispatch<SetStateAction<Partial<LLMRoleConfigMap>>>;
  setEditGenerationTemplateId?: Dispatch<SetStateAction<string>>;
  onClose: () => void;
  onSave: (templateToApply?: GenerationTemplate) => Promise<void>;
  setPendingTemplate: Dispatch<SetStateAction<GenerationTemplate | null>>;
  setEditImagePlatform: Dispatch<SetStateAction<string>>;
  setEditImageModel: Dispatch<SetStateAction<string>>;
  setEditJimengModel: Dispatch<SetStateAction<string>>;
  setEditBaseImagePlatform: Dispatch<SetStateAction<string>>;
  setEditBaseImageModel: Dispatch<SetStateAction<string>>;
  setEditVideoPlatform: Dispatch<SetStateAction<string>>;
  setEditVideoModel: Dispatch<SetStateAction<string>>;
  setEditCharFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditCharProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditSceneFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditSceneProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditPropFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditPropProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditCharStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditSceneStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditPropStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditCharStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditSceneStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditPropStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditVideoPrefixPrompt: Dispatch<SetStateAction<string>>;
  setEditVideoExtraPrompt: Dispatch<SetStateAction<string>>;
  setEditBaseConfigCompleted: Dispatch<SetStateAction<boolean>>;
}

function AdvancedSettingsSection({ t, projectId, activeImageModels, activeVideoModels, editImagePlatform, editImageModel, editBaseImagePlatform, editBaseImageModel, editVideoPlatform, editVideoModel, editCharFixedPrompt, editCharProjectRefImages, editSceneFixedPrompt, editSceneProjectRefImages, editPropFixedPrompt, editPropProjectRefImages, editCharStatePromptPrefix, editSceneStatePromptPrefix, editPropStatePromptPrefix, editCharStateImageRatio, editSceneStateImageRatio, editPropStateImageRatio, editVideoPrefixPrompt, editVideoExtraPrompt, llmProviders, editLLMRoleOverrides, setEditImagePlatform, setEditImageModel, setEditJimengModel, setEditBaseImagePlatform, setEditBaseImageModel, setEditVideoPlatform, setEditVideoModel, setEditCharFixedPrompt, setEditCharProjectRefImages, setEditSceneFixedPrompt, setEditSceneProjectRefImages, setEditPropFixedPrompt, setEditPropProjectRefImages, setEditCharStatePromptPrefix, setEditSceneStatePromptPrefix, setEditPropStatePromptPrefix, setEditCharStateImageRatio, setEditSceneStateImageRatio, setEditPropStateImageRatio, setEditVideoPrefixPrompt, setEditVideoExtraPrompt, setEditLLMRoleOverrides }: {
  t: Translate;
  projectId?: string;
  activeImageModels: ImageModel[];
  activeVideoModels: VideoModel[];
  editImagePlatform: string;
  editImageModel: string;
  editBaseImagePlatform: string;
  editBaseImageModel: string;
  editVideoPlatform: string;
  editVideoModel: string;
  editCharFixedPrompt: string;
  editCharProjectRefImages: string[];
  editSceneFixedPrompt: string;
  editSceneProjectRefImages: string[];
  editPropFixedPrompt: string;
  editPropProjectRefImages: string[];
  editCharStatePromptPrefix: string;
  editSceneStatePromptPrefix: string;
  editPropStatePromptPrefix: string;
  editCharStateImageRatio: string;
  editSceneStateImageRatio: string;
  editPropStateImageRatio: string;
  editVideoPrefixPrompt: string;
  editVideoExtraPrompt: string;
  llmProviders: LLMProvider[];
  editLLMRoleOverrides: Partial<LLMRoleConfigMap>;
  setEditImagePlatform: Dispatch<SetStateAction<string>>;
  setEditImageModel: Dispatch<SetStateAction<string>>;
  setEditJimengModel: Dispatch<SetStateAction<string>>;
  setEditBaseImagePlatform: Dispatch<SetStateAction<string>>;
  setEditBaseImageModel: Dispatch<SetStateAction<string>>;
  setEditVideoPlatform: Dispatch<SetStateAction<string>>;
  setEditVideoModel: Dispatch<SetStateAction<string>>;
  setEditCharFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditCharProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditSceneFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditSceneProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditPropFixedPrompt: Dispatch<SetStateAction<string>>;
  setEditPropProjectRefImages: Dispatch<SetStateAction<string[]>>;
  setEditCharStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditSceneStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditPropStatePromptPrefix: Dispatch<SetStateAction<string>>;
  setEditCharStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditSceneStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditPropStateImageRatio: Dispatch<SetStateAction<string>>;
  setEditVideoPrefixPrompt: Dispatch<SetStateAction<string>>;
  setEditVideoExtraPrompt: Dispatch<SetStateAction<string>>;
  setEditLLMRoleOverrides: Dispatch<SetStateAction<Partial<LLMRoleConfigMap>>>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [llmExpanded, setLlmExpanded] = useState(false);

  return (
    <>
      <div
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(v => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{
            display: 'inline-block', fontSize: '0.65rem', color: 'var(--text-3)',
            transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}>&#9654;</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-2)' }}>{t('styleModal.advancedSettings')}</span>
        </div>
      </div>

      {expanded && (
        <>
          <PlatformModelSelectSection
            title={t('styleModal.baseImageModel')}
            titleTip={t('styleModal.baseImageModelTip')}
            models={activeImageModels}
            platform={editBaseImagePlatform}
            model={editBaseImageModel}
            emptyLabel={t('styleModal.noImageModels')}
            onPlatformChange={(platform, firstModel) => {
              setEditBaseImagePlatform(platform);
              if (firstModel) setEditBaseImageModel(firstModel);
            }}
            onModelChange={setEditBaseImageModel}
          />

          <PlatformModelSelectSection
            title={t('styleModal.stateImageModel')}
            titleTip={t('styleModal.stateImageModelTip')}
            models={activeImageModels}
            platform={editImagePlatform}
            model={editImageModel}
            emptyLabel={t('styleModal.noImageModels')}
            onPlatformChange={(platform, firstModel) => {
              setEditImagePlatform(platform);
              if (firstModel) {
                setEditImageModel(firstModel);
                setEditJimengModel(firstModel);
              }
            }}
            onModelChange={model => {
              setEditImageModel(model);
              setEditJimengModel(model);
            }}
          />

          <PlatformModelSelectSection
            title={t('styleModal.videoModel')}
            titleTip={t('styleModal.videoModelTip')}
            models={activeVideoModels}
            platform={editVideoPlatform}
            model={editVideoModel}
            emptyLabel={t('styleModal.noVideoModels')}
            onPlatformChange={(platform, firstModel) => {
              setEditVideoPlatform(platform);
              if (firstModel) setEditVideoModel(firstModel);
            }}
            onModelChange={setEditVideoModel}
          />

          {(['character', 'scene', 'prop'] as const).map(entityType => {
            const label = entityType === 'character' ? t('styleModal.character') : entityType === 'scene' ? t('styleModal.scene') : t('styleModal.prop');
            const fixedPrompt = entityType === 'character' ? editCharFixedPrompt : entityType === 'scene' ? editSceneFixedPrompt : editPropFixedPrompt;
            const setFixedPrompt = entityType === 'character' ? setEditCharFixedPrompt : entityType === 'scene' ? setEditSceneFixedPrompt : setEditPropFixedPrompt;
            const refImages = entityType === 'character' ? editCharProjectRefImages : entityType === 'scene' ? editSceneProjectRefImages : editPropProjectRefImages;
            const setRefImages = entityType === 'character' ? setEditCharProjectRefImages : entityType === 'scene' ? setEditSceneProjectRefImages : setEditPropProjectRefImages;
            const maxProjectRefs = MAX_TOTAL_REF_IMAGES;
            return (
              <BaseImageConfigSection
                key={entityType}
                projectId={projectId}
                entityType={entityType}
                title={t('styleModal.baseImageConfig', { type: label })}
                titleTip={t('styleModal.baseImageConfigTip', { type: label })}
                fixedPromptLabel={t('styleModal.fixedPrompt')}
                fixedPromptPlaceholder="参考图片来生成图片"
                projectRefImagesLabel={t('styleModal.projectRefImages', { current: refImages.length, max: maxProjectRefs })}
                fixedPrompt={fixedPrompt}
                projectRefImages={refImages}
                onFixedPromptChange={setFixedPrompt}
                onProjectRefImagesChange={setRefImages}
              />
            );
          })}

          <StatePromptPrefixSection
            title={t('styleModal.charStatePromptPrefix')}
            description={t('styleModal.charStatePromptPrefixDesc')}
            value={editCharStatePromptPrefix}
            rows={3}
            ratio={editCharStateImageRatio}
            placeholder={DEFAULT_CHAR_STATE_PROMPT_PREFIX}
            actionLabel={t('styleModal.restoreDefault')}
            outputRatioLabel={t('styleModal.outputRatio')}
            onChange={setEditCharStatePromptPrefix}
            onRatioChange={setEditCharStateImageRatio}
            onAction={() => setEditCharStatePromptPrefix(DEFAULT_CHAR_STATE_PROMPT_PREFIX)}
          />

          <StatePromptPrefixSection
            title={t('styleModal.sceneStatePromptPrefix')}
            description={t('styleModal.sceneStatePromptPrefixDesc')}
            value={editSceneStatePromptPrefix}
            rows={2}
            ratio={editSceneStateImageRatio}
            placeholder={t('styleModal.emptyPrefixPlaceholder')}
            actionLabel={editSceneStatePromptPrefix ? t('styleModal.clear') : undefined}
            outputRatioLabel={t('styleModal.outputRatio')}
            onChange={setEditSceneStatePromptPrefix}
            onRatioChange={setEditSceneStateImageRatio}
            onAction={() => setEditSceneStatePromptPrefix('')}
          />

          <StatePromptPrefixSection
            title={t('styleModal.propStatePromptPrefix')}
            description={t('styleModal.propStatePromptPrefixDesc')}
            value={editPropStatePromptPrefix}
            rows={2}
            ratio={editPropStateImageRatio}
            placeholder={t('styleModal.emptyPrefixPlaceholder')}
            actionLabel={editPropStatePromptPrefix ? t('styleModal.clear') : undefined}
            outputRatioLabel={t('styleModal.outputRatio')}
            onChange={setEditPropStatePromptPrefix}
            onRatioChange={setEditPropStateImageRatio}
            onAction={() => setEditPropStatePromptPrefix('')}
          />

          <PromptTextareaSection
            title={t('styleModal.videoPrefixPrompt')}
            helpText={t('styleModal.videoPrefixPromptTip')}
            description={t('styleModal.videoPrefixPromptDesc')}
            value={editVideoPrefixPrompt}
            rows={3}
            placeholder={t('styleModal.videoPrefixPromptPlaceholder')}
            onChange={setEditVideoPrefixPrompt}
          />

          <PromptTextareaSection
            title={t('styleModal.videoExtraPrompt')}
            helpText={t('styleModal.videoExtraPromptTip')}
            description={t('styleModal.videoExtraPromptDesc')}
            value={editVideoExtraPrompt}
            rows={3}
            placeholder={t('styleModal.videoExtraPromptPlaceholder')}
            onChange={setEditVideoExtraPrompt}
          />

          <>
            <div
              style={{ cursor: 'pointer', userSelect: 'none', marginTop: '0.5rem' }}
              onClick={() => setLlmExpanded(v => !v)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{
                  display: 'inline-block', fontSize: '0.6rem', color: 'var(--text-3)',
                  transition: 'transform 0.2s', transform: llmExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>&#9654;</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-2)' }}>{t('styleModal.llmLabel')}</span>
                <HelpTip text={t('styleModal.llmHelp')} />
              </div>
            </div>
            {llmExpanded && (
              <LLMRoleOverridesSection
                providers={llmProviders}
                roleOverrides={editLLMRoleOverrides}
                roles={buildLLMRoleOverrideOptions(t, 'styleModal')}
                title=""
                titleTip=""
                globalDefaultLabel={t('styleModal.llmGlobalDefault')}
                providerDisabledLabel={t('styleModal.llmDisabled')}
                providerUnavailableLabel={t('styleModal.llmUnavailable')}
                selectModelLabel={t('styleModal.llmSelectModel')}
                emptyLabel={t('styleModal.llmEmpty')}
                onRoleOverridesChange={setEditLLMRoleOverrides}
              />
            )}
          </>
        </>
      )}
    </>
  );
}

const INLINE_MAX = 6;

function StyleCard({ template, isSelected, t, onClick }: {
  template: GenerationTemplate; isSelected: boolean; t: Translate; onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface-3)', borderRadius: '8px', overflow: 'hidden',
        border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
        cursor: 'pointer', transition: 'border-color 0.15s',
      }}
    >
      <div style={{ aspectRatio: '3/4', overflow: 'hidden', position: 'relative' }}>
        <img
          src={tosAccelerate(template.coverImageUrl || '/template-cover-placeholder.png')}
          alt={template.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {template.isDefault && (
          <span style={{ position: 'absolute', top: '4px', left: '4px', background: 'var(--primary)', color: '#fff', fontSize: '0.6rem', padding: '1px 6px', borderRadius: '8px' }}>
            {t('styleModal.presetDefault')}
          </span>
        )}
      </div>
      <div style={{ padding: '0.4rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{template.name}</div>
      </div>
    </div>
  );
}

function StylePresetSection({ t, templates, selected, hasCustomEdits, isExplicitlySelected, onSelect }: {
  t: Translate;
  templates: GenerationTemplate[];
  selected: GenerationTemplate | null;
  hasCustomEdits: boolean;
  isExplicitlySelected: boolean;
  onSelect: (tpl: GenerationTemplate) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);

  const handleSelect = (tpl: GenerationTemplate) => {
    onSelect(tpl);
    setPickerOpen(false);
  };

  const handleChangeClick = () => {
    if (hasCustomEdits) {
      setShowChangeWarning(true);
    } else {
      setPickerOpen(true);
    }
  };

  if (isExplicitlySelected && selected) {
    return (
      <>
        <label className="form-label" style={{ marginBottom: '-0.25rem', fontSize: '0.9rem' }}>
          {t('styleModal.currentStyle')}<HelpTip text={t('styleModal.applyPresetTip')} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem', alignItems: 'stretch' }}>
          <div style={{ position: 'relative' }}>
            <StyleCard template={selected} isSelected t={t} onClick={handleChangeClick} />
            {hasCustomEdits && (
              <span style={{
                position: 'absolute', top: '4px', right: '4px',
                background: 'var(--warning, #e6a23c)', color: '#fff',
                fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px',
                borderRadius: '4px', whiteSpace: 'nowrap', zIndex: 2,
              }}>
                {t('styleModal.customEdited')}
              </span>
            )}
          </div>
          <button
            onClick={handleChangeClick}
            style={{
              background: 'none', border: '1px dashed var(--primary)', color: 'var(--primary)',
              borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {t('styleModal.changeStyle')}
          </button>
        </div>

        {showChangeWarning && (
          <div style={{
            marginTop: '0.5rem', padding: '0.6rem 0.8rem', borderRadius: '6px',
            background: 'rgba(230, 162, 60, 0.1)', border: '1px solid var(--warning, #e6a23c)',
            display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.82rem', color: 'var(--text-2)',
          }}>
            <span style={{ flex: 1 }}>{t('styleModal.changeStyleWarning')}</span>
            <button
              onClick={() => { setShowChangeWarning(false); setPickerOpen(true); }}
              style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('confirm', { ns: 'common' })}
            </button>
            <button
              onClick={() => setShowChangeWarning(false)}
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-2)', borderRadius: '4px', padding: '4px 12px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        )}

        <Modal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={t('styleModal.chooseStyle')}
          width="clamp(800px, 80vw, 1280px)"
          footer={
            <button className="btn btn-secondary" onClick={() => setPickerOpen(false)}>
              {t('close', { ns: 'common' })}
            </button>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem' }}>
            {templates.map(tpl => (
              <StyleCard key={tpl.id} template={tpl} isSelected={selected?.id === tpl.id} t={t} onClick={() => handleSelect(tpl)} />
            ))}
          </div>
        </Modal>
      </>
    );
  }

  const hasOverflow = templates.length > INLINE_MAX;
  const inlineTemplates = hasOverflow ? templates.slice(0, INLINE_MAX - 1) : templates;

  return (
    <>
      <label className="form-label" style={{ marginBottom: '-0.25rem', fontSize: '0.9rem' }}>
        {t('styleModal.selectStyle')}<HelpTip text={t('styleModal.applyPresetTip')} />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem' }}>
        {inlineTemplates.map(tpl => (
          <StyleCard key={tpl.id} template={tpl} isSelected={selected?.id === tpl.id} t={t} onClick={() => onSelect(tpl)} />
        ))}
        {hasOverflow && (
          <div
            onClick={() => setPickerOpen(true)}
            style={{
              background: 'var(--surface-3)', borderRadius: '8px', overflow: 'hidden',
              border: '1px solid var(--border)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              aspectRatio: '3/5', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-2)',
            }}
          >
            {t('styleModal.more')}
          </div>
        )}
      </div>

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('styleModal.chooseStyle')}
        width="clamp(800px, 80vw, 1280px)"
        footer={
          <button className="btn btn-secondary" onClick={() => setPickerOpen(false)}>
            {t('close', { ns: 'common' })}
          </button>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem' }}>
          {templates.map(tpl => (
            <StyleCard key={tpl.id} template={tpl} isSelected={selected?.id === tpl.id} t={t} onClick={() => handleSelect(tpl)} />
          ))}
        </div>
      </Modal>
    </>
  );
}

export default function StyleConfigModal({
  open,
  saving,
  projectId,
  dismissible = true,
  t,
  generationTemplates,
  pendingTemplate,
  selectedTemplateId,
  activeImageModels,
  activeVideoModels,
  editImagePlatform,
  editImageModel,
  editBaseImagePlatform,
  editBaseImageModel,
  editVideoPlatform,
  editVideoModel,
  editCharFixedPrompt,
  editCharProjectRefImages,
  editSceneFixedPrompt,
  editSceneProjectRefImages,
  editPropFixedPrompt,
  editPropProjectRefImages,
  editCharStatePromptPrefix,
  editSceneStatePromptPrefix,
  editPropStatePromptPrefix,
  editCharStateImageRatio,
  editSceneStateImageRatio,
  editPropStateImageRatio,
  editVideoPrefixPrompt,
  editVideoExtraPrompt,
  editBaseConfigCompleted,
  llmProviders,
  editLLMRoleOverrides,
  setEditLLMRoleOverrides,
  onClose,
  onSave,
  setPendingTemplate,
  setEditImagePlatform,
  setEditImageModel,
  setEditJimengModel,
  setEditBaseImagePlatform,
  setEditBaseImageModel,
  setEditVideoPlatform,
  setEditVideoModel,
  setEditCharFixedPrompt,
  setEditCharProjectRefImages,
  setEditSceneFixedPrompt,
  setEditSceneProjectRefImages,
  setEditPropFixedPrompt,
  setEditPropProjectRefImages,
  setEditCharStatePromptPrefix,
  setEditSceneStatePromptPrefix,
  setEditPropStatePromptPrefix,
  setEditCharStateImageRatio,
  setEditSceneStateImageRatio,
  setEditPropStateImageRatio,
  setEditVideoPrefixPrompt,
  setEditVideoExtraPrompt,
  setEditBaseConfigCompleted,
  setEditGenerationTemplateId,
}: StyleConfigModalProps) {
  const [sessionEdited, setSessionEdited] = useState(false);

  const isExplicitlySelected = !!selectedTemplateId;
  const effectiveSelected = pendingTemplate || generationTemplates.find(tpl => tpl.id === selectedTemplateId) || generationTemplates.find(tpl => tpl.isDefault) || null;

  const savedTemplateDiffers = useMemo(() => {
    if (!isExplicitlySelected) return false;
    const tpl = generationTemplates.find(t => t.id === selectedTemplateId);
    if (!tpl) return false;
    const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    return !eq(editCharFixedPrompt, tpl.charFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT)
      || !eq(editCharProjectRefImages, parseRefImages(tpl.charProjectRefImages))
      || !eq(editSceneFixedPrompt, tpl.sceneFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT)
      || !eq(editSceneProjectRefImages, parseRefImages(tpl.sceneProjectRefImages))
      || !eq(editPropFixedPrompt, tpl.propFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT)
      || !eq(editPropProjectRefImages, parseRefImages(tpl.propProjectRefImages))
      || !eq(editCharStatePromptPrefix, tpl.charStatePromptPrefix || DEFAULT_CHAR_STATE_PROMPT_PREFIX)
      || !eq(editSceneStatePromptPrefix, tpl.sceneStatePromptPrefix || '')
      || !eq(editPropStatePromptPrefix, tpl.propStatePromptPrefix || '')
      || !eq(editCharStateImageRatio, tpl.charStateImageRatio || '16:9')
      || !eq(editSceneStateImageRatio, tpl.sceneStateImageRatio || '16:9')
      || !eq(editPropStateImageRatio, tpl.propStateImageRatio || '16:9')
      || !eq(editVideoPrefixPrompt, tpl.videoPrefixPrompt || '')
      || !eq(editVideoExtraPrompt, tpl.videoExtraPrompt || '');
  }, [isExplicitlySelected, selectedTemplateId, generationTemplates, editCharFixedPrompt, editCharProjectRefImages, editSceneFixedPrompt, editSceneProjectRefImages, editPropFixedPrompt, editPropProjectRefImages, editCharStatePromptPrefix, editSceneStatePromptPrefix, editPropStatePromptPrefix, editCharStateImageRatio, editSceneStateImageRatio, editPropStateImageRatio, editVideoPrefixPrompt, editVideoExtraPrompt]);

  const hasCustomEdits = sessionEdited || savedTemplateDiffers;

  const wrapSetter = <T,>(setter: Dispatch<SetStateAction<T>>): Dispatch<SetStateAction<T>> => {
    return (val) => { setSessionEdited(true); setter(val); };
  };

  const applyTemplateValues = (template: GenerationTemplate) => {
    if (template.imagePlatform) setEditImagePlatform(template.imagePlatform);
    if (template.imageModel) {
      setEditImageModel(template.imageModel);
      setEditJimengModel(template.imageModel);
    }
    setEditBaseImagePlatform(template.baseImagePlatform || '');
    setEditBaseImageModel(template.baseImageModel || '');
    if (template.videoPlatform) setEditVideoPlatform(template.videoPlatform);
    if (template.videoModel) setEditVideoModel(template.videoModel);
    setEditCharFixedPrompt(template.charFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditCharProjectRefImages(parseRefImages(template.charProjectRefImages));
    setEditSceneFixedPrompt(template.sceneFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditSceneProjectRefImages(parseRefImages(template.sceneProjectRefImages));
    setEditPropFixedPrompt(template.propFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditPropProjectRefImages(parseRefImages(template.propProjectRefImages));
    setEditCharStatePromptPrefix(template.charStatePromptPrefix || DEFAULT_CHAR_STATE_PROMPT_PREFIX);
    setEditSceneStatePromptPrefix(template.sceneStatePromptPrefix || '');
    setEditPropStatePromptPrefix(template.propStatePromptPrefix || '');
    setEditCharStateImageRatio(template.charStateImageRatio || '16:9');
    setEditSceneStateImageRatio(template.sceneStateImageRatio || '16:9');
    setEditPropStateImageRatio(template.propStateImageRatio || '16:9');
    setEditVideoPrefixPrompt(template.videoPrefixPrompt || '');
    setEditVideoExtraPrompt(template.videoExtraPrompt || '');
    setEditLLMRoleOverrides(template.llmRoleOverrides || {});
  };

  const handleSave = async () => {
    const templateToApply = pendingTemplate || (!selectedTemplateId ? effectiveSelected : null);
    if (templateToApply) {
      applyTemplateValues(templateToApply);
      setEditBaseConfigCompleted(true);
      setEditGenerationTemplateId?.(templateToApply.id);
    }
    await onSave(templateToApply || undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={dismissible}
      solidBackdrop={!dismissible}
      title={t('styleModal.title')}
      width="clamp(800px, 80vw, 1280px)"
      footer={
        <>
          {dismissible && <button className="btn btn-secondary" onClick={onClose}>{t('cancel', { ns: 'common' })}</button>}
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? t('savingEllipsis', { ns: 'common' }) : t('save', { ns: 'common' })}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {generationTemplates.length > 0 && (
          <StylePresetSection
            t={t}
            templates={generationTemplates}
            selected={effectiveSelected}
            hasCustomEdits={hasCustomEdits}
            isExplicitlySelected={isExplicitlySelected}
            onSelect={(tpl) => {
              setPendingTemplate(tpl);
              setEditBaseConfigCompleted(true);
              setSessionEdited(false);
            }}
          />
        )}

        <AdvancedSettingsSection
          t={t}
          projectId={projectId}
          activeImageModels={activeImageModels}
          activeVideoModels={activeVideoModels}
          editImagePlatform={editImagePlatform}
          editImageModel={editImageModel}
          editBaseImagePlatform={editBaseImagePlatform}
          editBaseImageModel={editBaseImageModel}
          editVideoPlatform={editVideoPlatform}
          editVideoModel={editVideoModel}
          editCharFixedPrompt={editCharFixedPrompt}
          editCharProjectRefImages={editCharProjectRefImages}
          editSceneFixedPrompt={editSceneFixedPrompt}
          editSceneProjectRefImages={editSceneProjectRefImages}
          editPropFixedPrompt={editPropFixedPrompt}
          editPropProjectRefImages={editPropProjectRefImages}
          editCharStatePromptPrefix={editCharStatePromptPrefix}
          editSceneStatePromptPrefix={editSceneStatePromptPrefix}
          editPropStatePromptPrefix={editPropStatePromptPrefix}
          editCharStateImageRatio={editCharStateImageRatio}
          editSceneStateImageRatio={editSceneStateImageRatio}
          editPropStateImageRatio={editPropStateImageRatio}
          editVideoPrefixPrompt={editVideoPrefixPrompt}
          editVideoExtraPrompt={editVideoExtraPrompt}
          setEditImagePlatform={wrapSetter(setEditImagePlatform)}
          setEditImageModel={wrapSetter(setEditImageModel)}
          setEditJimengModel={wrapSetter(setEditJimengModel)}
          setEditBaseImagePlatform={wrapSetter(setEditBaseImagePlatform)}
          setEditBaseImageModel={wrapSetter(setEditBaseImageModel)}
          setEditVideoPlatform={wrapSetter(setEditVideoPlatform)}
          setEditVideoModel={wrapSetter(setEditVideoModel)}
          setEditCharFixedPrompt={wrapSetter(setEditCharFixedPrompt)}
          setEditCharProjectRefImages={wrapSetter(setEditCharProjectRefImages)}
          setEditSceneFixedPrompt={wrapSetter(setEditSceneFixedPrompt)}
          setEditSceneProjectRefImages={wrapSetter(setEditSceneProjectRefImages)}
          setEditPropFixedPrompt={wrapSetter(setEditPropFixedPrompt)}
          setEditPropProjectRefImages={wrapSetter(setEditPropProjectRefImages)}
          setEditCharStatePromptPrefix={wrapSetter(setEditCharStatePromptPrefix)}
          setEditSceneStatePromptPrefix={wrapSetter(setEditSceneStatePromptPrefix)}
          setEditPropStatePromptPrefix={wrapSetter(setEditPropStatePromptPrefix)}
          setEditCharStateImageRatio={wrapSetter(setEditCharStateImageRatio)}
          setEditSceneStateImageRatio={wrapSetter(setEditSceneStateImageRatio)}
          setEditPropStateImageRatio={wrapSetter(setEditPropStateImageRatio)}
          setEditVideoPrefixPrompt={wrapSetter(setEditVideoPrefixPrompt)}
          setEditVideoExtraPrompt={wrapSetter(setEditVideoExtraPrompt)}
          llmProviders={llmProviders}
          editLLMRoleOverrides={editLLMRoleOverrides}
          setEditLLMRoleOverrides={wrapSetter(setEditLLMRoleOverrides)}
        />
      </div>

    </Modal>
  );
}
